import { language } from "../i18n";
import type { Branding } from "../brand";
import type { Account, AgentToken, APIToken, MFAStatus, Passkey, SessionInfo, SignInAnswer, Artifact, Bookmark, Huddle, LinkPreview, DiscoveryItem, Preferences, QuietHours, Shortcut, Matter, ArtifactEntry, ArtifactVersion, Budget, Capability, CatchUp, InboxItem, Channel, ChannelEvent, ChannelFile, Delegation, DirectoryGroup, DLPRule, FileQuery, Fleet, FollowedThread, CustomEmoji, Group, Invite, PasswordLink, PasswordRequest, Pin, Reminder, ScheduledMessage, SidebarLayout, SavedMessage, HealthReport, HostedAgent, LicenseStatus, MCPServer, Message, ModelBackend, ModelBackendInput, ModelTest, PastHuddle, PostedFile, Principal, ReauthInfo, Span, Task, TaskEvent, TraceSummary, UUID, ViewResult, Workflow, WorkflowRun, WorkspaceSettings } from "./types";
import type { AgentPool, CapabilityCard, ManagedAgent, TaskPolicy } from "./types";
import type { NewTask } from "./types";

// GroupMiss is someone a group could not put in one of its channels.
export type GroupMiss = { principal_id: UUID; channel_id: UUID; error: string };

// GroupChange is what to make a group with, or change about one.
type GroupChange = { handle?: string; name?: string; description?: string; member_ids?: UUID[]; role?: string; channel_ids?: UUID[]; require_mfa?: boolean };

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

export interface EventPage {
  events: ChannelEvent[];
  last_seq: number;
  has_more: boolean;
}

export interface FileInfo {
  id: UUID;
  name: string;
  mime: string;
  size: number;
}

export interface SearchAnswer {
  answer: string;
  citations: { n: number; message: Message; permalink: string }[];
  reason?: string;
  hits?: SearchHit[];
}

export interface SearchHit {
  message: Message;
  snippet: string;
  rank: number;
  permalink: string;
}

// Api is the native REST API (/api/v1). fetchImpl is injectable for tests.
export class Api {
  // onMFASetup is called when the server says the signed-in person must set
  // up two-step verification first, as their groups came to need it.
  static onMFASetup?: () => void;
  // onReauth is called when the server wants the signed-in person to confirm
  // it is them first (ADR-0026); true means they did, and the request goes
  // again.
  static onReauth?: () => Promise<boolean>;

  constructor(
    private readonly base: string,
    private token: string | null,
    private readonly fetchImpl: typeof fetch = (...a) => fetch(...a),
  ) {}

  setToken(token: string | null) {
    this.token = token;
  }

  getToken() {
    return this.token;
  }

  // historyTimeoutMs is how long a channel's history may take before it is
  // given up on, to be tried again: a connection gone dead, as after a
  // laptop sleeps, would otherwise leave it loading for good.
  historyTimeoutMs = 20_000;

  async request<T>(method: string, path: string, body?: unknown, headers: Record<string, string> = {}, timeoutMs?: number): Promise<T> {
    try {
      return await this.send<T>(method, path, body, headers, timeoutMs);
    } catch (err) {
      if (err instanceof ApiError && err.code === "reauth_required" && (await Api.onReauth?.())) return this.send<T>(method, path, body, headers, timeoutMs);
      throw err;
    }
  }

  private async send<T>(method: string, path: string, body: unknown, headers: Record<string, string>, timeoutMs?: number): Promise<T> {
    // Errors come back in the reader's language.
    const h: Record<string, string> = { "Accept-Language": language, ...headers };
    if (this.token) h["Authorization"] = `Bearer ${this.token}`;
    if (body !== undefined) h["Content-Type"] = "application/json";
    const res = await this.fetchImpl(this.base + path, {
      method,
      headers: h,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: timeoutMs ? AbortSignal.timeout(timeoutMs) : undefined,
    });
    const data = (await res.json().catch(() => ({}))) as { error?: { code: string; message: string } };
    if (!res.ok) {
      if (data.error?.code === "mfa_setup_required") Api.onMFASetup?.();
      throw new ApiError(res.status, data.error?.code ?? "http_" + res.status, data.error?.message ?? res.statusText);
    }
    return data as T;
  }

  // reauthMethods says how the signed-in person can confirm it is them, and
  // until when their session counts as recent.
  reauthMethods() {
    return this.request<ReauthInfo>("GET", "/auth/reauth");
  }

  reauth(proof: { password: string } | { code: string }) {
    return this.request<{ until: string }>("POST", "/auth/reauth", proof);
  }

  beginReauthPasskey() {
    return this.request<{ pending: string; options: Record<string, unknown> }>("POST", "/auth/reauth/passkey/begin", {});
  }

  finishReauthPasskey(pending: string, credential: unknown) {
    return this.request<{ until: string }>("POST", "/auth/reauth/passkey/finish", { pending, credential });
  }

  // reauthSSO answers with where a window of its own confirms it through
  // the identity provider.
  reauthSSO() {
    return this.request<{ url: string }>("POST", "/auth/reauth/sso", {});
  }

  // login answers with a session token, or, with two-step verification
  // on, what second factor to send to secondFactor.
  login(handle: string, password: string) {
    return this.request<SignInAnswer>("POST", "/auth/login", { handle, password });
  }

  secondFactor(pending: string, code: string) {
    return this.request<SignInAnswer>("POST", "/auth/second-factor", { pending, code });
  }

  mfa() {
    return this.request<{ mfa: MFAStatus }>("GET", "/me/mfa");
  }

  startTOTP(password: string) {
    return this.request<{ secret: string; uri: string; qr: string }>("POST", "/me/mfa/totp", { password });
  }

  confirmTOTP(code: string) {
    return this.request<{ recovery_codes: string[] }>("POST", "/me/mfa/totp/confirm", { code });
  }

  disableTOTP(code: string) {
    return this.request<{ ok: boolean }>("POST", "/me/mfa/totp/disable", { code });
  }

  renewRecoveryCodes(code: string) {
    return this.request<{ recovery_codes: string[] }>("POST", "/me/mfa/recovery-codes", { code });
  }

  passkeys() {
    return this.request<{ passkeys: Passkey[] }>("GET", "/me/passkeys");
  }

  beginPasskey(password: string, name: string) {
    return this.request<{ pending: string; options: Record<string, unknown> }>("POST", "/me/passkeys/begin", { password, name });
  }

  finishPasskey(pending: string, credential: Record<string, unknown>) {
    return this.request<{ passkey: Passkey }>("POST", "/me/passkeys/finish", { pending, credential });
  }

  renamePasskey(id: UUID, name: string) {
    return this.request<{ ok: boolean }>("PATCH", `/me/passkeys/${id}`, { name });
  }

  removePasskey(id: UUID) {
    return this.request<{ ok: boolean }>("DELETE", `/me/passkeys/${id}`);
  }

  // Signing in with a passkey alone, or as a sign-in's second step.
  beginPasskeySignIn() {
    return this.request<{ pending: string; options: Record<string, unknown> }>("POST", "/auth/passkey/begin", {});
  }

  finishPasskeySignIn(pending: string, credential: Record<string, unknown>) {
    return this.request<SignInAnswer>("POST", "/auth/passkey/finish", { pending, credential });
  }

  beginPasskeySecondStep(pending: string) {
    return this.request<{ pending: string; options: Record<string, unknown> }>("POST", "/auth/second-factor/passkey/begin", { pending });
  }

  finishPasskeySecondStep(pending: string, ceremony: string, credential: Record<string, unknown>) {
    return this.request<SignInAnswer>("POST", "/auth/second-factor/passkey/finish", { pending, ceremony, credential });
  }

  resetMFA(id: UUID) {
    return this.request<{ ok: boolean }>("POST", `/admin/users/${id}/reset-mfa`, {});
  }

  logout() {
    return this.request<{ ok: boolean }>("POST", "/auth/logout");
  }

  me() {
    return this.request<{ principal: Principal; scopes: string[]; workspace?: { id: UUID; name: string; theme?: string; recall_seconds?: number; channel_creation?: "everyone" | "admins" }; mfa_setup_required?: boolean }>("GET", "/me");
  }

  listChannels() {
    return this.request<{ channels: Channel[]; head_cursor?: string }>("GET", "/channels");
  }

  getChannel(id: UUID) {
    return this.request<{ channel: Channel }>("GET", `/channels/${id}`);
  }

  createChannel(name: string, kind: "public" | "private", topic = "") {
    return this.request<{ channel: Channel }>("POST", "/channels", { name, kind, topic });
  }

  joinChannel(id: UUID) {
    return this.request<{ channel: Channel }>("POST", `/channels/${id}/join`);
  }

  openDM(principalIDs: UUID[]) {
    return this.request<{ channel: Channel }>("POST", "/dms", { principal_ids: principalIDs });
  }

  events(channel: UUID, afterSeq: number, limit = 500) {
    return this.request<EventPage>("GET", `/channels/${channel}/events?after_seq=${afterSeq}&limit=${limit}`);
  }

  history(channel: UUID, beforeSeq = 0, limit = 50) {
    const before = beforeSeq > 0 ? `&before_seq=${beforeSeq}` : "";
    return this.request<{ messages: Message[] }>("GET", `/channels/${channel}/messages?limit=${limit}${before}`, undefined, {}, this.historyTimeoutMs);
  }

  thread(channel: UUID, root: UUID) {
    return this.request<{ root: Message; replies: Message[] }>("GET", `/channels/${channel}/threads/${root}`);
  }

  post(channel: UUID, text: string, clientMsgID: string, threadRootID: UUID | null = null, fileIDs: UUID[] = [], alsoInChannel = false) {
    return this.request<{ message: Message }>(
      "POST",
      `/channels/${channel}/messages`,
      { text, client_msg_id: clientMsgID, thread_root_id: threadRootID, file_ids: fileIDs, also_in_channel: alsoInChannel },
      { "Idempotency-Key": clientMsgID },
    );
  }

  // upload sends a file's bytes as the request body.
  async upload(file: Blob, name: string) {
    const res = await this.fetchImpl(`${this.base}/files?name=${encodeURIComponent(name)}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${this.token}`, "Accept-Language": language, "Content-Type": file.type || "application/octet-stream" },
      body: file,
    });
    const data = (await res.json().catch(() => ({}))) as { file?: FileInfo; url?: string; error?: { code: string; message: string } };
    if (!res.ok || !data.file) throw new ApiError(res.status, data.error?.code ?? "upload_failed", data.error?.message ?? res.statusText);
    return { file: data.file, url: data.url ?? "" };
  }

  file(id: UUID) {
    return this.request<{ file: FileInfo; url: string }>("GET", `/files/${id}`);
  }

  edit(channel: UUID, message: UUID, text: string) {
    return this.request<{ message: Message }>("PATCH", `/channels/${channel}/messages/${message}`, { text });
  }

  remove(channel: UUID, message: UUID) {
    return this.request<{ ok: boolean }>("DELETE", `/channels/${channel}/messages/${message}`);
  }

  branding() {
    return this.request<{ branding: Branding }>("GET", "/branding");
  }

  setBranding(b: Pick<Branding, "site_name" | "sign_in_message" | "accent">) {
    return this.request<{ branding: Branding }>("PUT", "/admin/branding", b);
  }

  // setLogo takes the logo as square PNGs, base64'd, at 192 and 512 pixels.
  setLogo(icon192: string, icon512: string) {
    return this.request<{ branding: Branding }>("PUT", "/admin/branding/logo", { icon_192: icon192, icon_512: icon512 });
  }

  removeLogo() {
    return this.request<{ branding: Branding }>("DELETE", "/admin/branding/logo");
  }

  recall(channel: UUID, message: UUID) {
    return this.request<{ message: Message }>("POST", `/channels/${channel}/messages/${message}/recall`);
  }

  markUnread(channel: UUID, seq: number) {
    return this.request<{ last_read_seq: number }>("POST", `/channels/${channel}/unread`, { seq });
  }

  markRead(channel: UUID, seq: number) {
    return this.request<{ last_read_seq: number }>("POST", `/channels/${channel}/read`, { seq });
  }

  react(channel: UUID, message: UUID, emoji: string, add: boolean) {
    return add
      ? this.request<{ ok: boolean }>("POST", `/channels/${channel}/messages/${message}/reactions`, { emoji })
      : this.request<{ ok: boolean }>("DELETE", `/channels/${channel}/messages/${message}/reactions/${encodeURIComponent(emoji)}`);
  }

  agentTraces(agent: UUID, before?: string) {
    const q = before ? `?before=${encodeURIComponent(before)}` : "";
    return this.request<{ traces: TraceSummary[] }>("GET", `/agents/${agent}/traces${q}`);
  }

  trace(id: UUID) {
    return this.request<{ trace_id: UUID; spans: Span[] }>("GET", `/traces/${id}`);
  }

  delegations() {
    return this.request<{ delegations: Delegation[] }>("GET", "/delegations");
  }

  delegate(granteeID: UUID, scopes: string[], channelIDs: UUID[], days: number) {
    return this.request<{ delegation: Delegation }>("POST", "/delegations", {
      grantee_id: granteeID,
      scopes,
      channel_ids: channelIDs,
      expires_in_days: days,
    });
  }

  revokeDelegation(id: UUID) {
    return this.request<{ ok: boolean }>("DELETE", `/delegations/${id}`);
  }

  presence() {
    return this.request<{ presence: Record<UUID, string> }>("GET", "/presence");
  }

  // principals is the directory: everyone, or only those active, or the
  // ones named (at most 200 at once).
  principals(which: { status?: "active"; ids?: UUID[]; q?: string; limit?: number } = {}) {
    const q = which.q
      ? `?q=${encodeURIComponent(which.q)}&limit=${which.limit ?? 12}`
      : which.ids
        ? `?ids=${which.ids.join(",")}`
        : which.status
          ? `?status=${which.status}`
          : "";
    return this.request<{ principals: Principal[] }>("GET", `/principals${q}`);
  }

  browse() {
    return this.request<{ channels: Channel[] }>("GET", "/channels/browse");
  }

  updateChannel(id: UUID, change: { name?: string; topic?: string; description?: string }) {
    return this.request<{ channel: Channel }>("PATCH", `/channels/${id}`, change);
  }

  makePrivate(id: UUID) {
    return this.request<{ channel: Channel }>("POST", `/channels/${id}/private`);
  }

  setArchived(id: UUID, archived: boolean) {
    return this.request<{ channel: Channel }>(archived ? "POST" : "DELETE", `/channels/${id}/archive`);
  }

  setClosed(id: UUID, closed: boolean) {
    return this.request<{ closed_at: string | null }>(closed ? "POST" : "DELETE", `/channels/${id}/close`);
  }
  // clearHistory clears a DM's history for oneself, on all one's devices.
  clearHistory(channel: UUID) {
    return this.request<{ cleared_seq: number }>("POST", `/channels/${channel}/clear`);
  }


  setNotifyLevel(id: UUID, level: "all" | "mentions" | "none") {
    return this.request<{ notify_level: string }>("PUT", `/channels/${id}/notifications`, { level });
  }

  sidebar() {
    return this.request<SidebarLayout>("GET", "/sidebar");
  }

  setSidebar(l: SidebarLayout) {
    return this.request<SidebarLayout>("PUT", "/sidebar", l);
  }

  reminders() {
    return this.request<{ reminders: Reminder[] }>("GET", "/reminders");
  }

  createReminder(r: { channel_id?: UUID; message_id?: UUID; text?: string; remind_at: string }) {
    return this.request<{ reminder: Reminder }>("POST", "/reminders", r);
  }

  snoozeReminder(id: UUID, remindAt: string) {
    return this.request<{ reminder: Reminder }>("PATCH", `/reminders/${id}`, { remind_at: remindAt });
  }

  completeReminder(id: UUID) {
    return this.request<{ ok: boolean }>("POST", `/reminders/${id}/done`);
  }

  scheduled() {
    return this.request<{ scheduled: ScheduledMessage[] }>("GET", "/scheduled");
  }

  scheduleMessage(channel: UUID, m: { text: string; thread_root_id?: UUID | null; send_at: string }) {
    return this.request<{ scheduled: ScheduledMessage }>("POST", `/channels/${channel}/scheduled`, m);
  }

  updateScheduled(id: UUID, change: { text?: string; send_at?: string }) {
    return this.request<{ scheduled: ScheduledMessage }>("PATCH", `/scheduled/${id}`, change);
  }

  deleteScheduled(id: UUID) {
    return this.request<{ ok: boolean }>("DELETE", `/scheduled/${id}`);
  }

  message(channel: UUID, id: UUID) {
    return this.request<{ message: Message }>("GET", `/channels/${channel}/messages/${id}`);
  }

  invites() {
    return this.request<{ invites: Invite[] }>("GET", "/admin/invites");
  }

  createInvite(n: { role: string; max_uses: number; days: number; email: string; channel_ids: UUID[] }) {
    return this.request<{ invite: Invite; token: string }>("POST", "/admin/invites", n);
  }

  revokeInvite(id: UUID) {
    return this.request<{ ok: boolean }>("DELETE", `/admin/invites/${id}`);
  }

  // accounts is everyone in the workspace as admins manage them.
  accounts() {
    return this.request<{ users: Account[] }>("GET", "/admin/users");
  }

  createAccount(n: { handle: string; display_name: string; email: string; role: string }) {
    return this.request<{ principal: Principal; link: PasswordLink }>("POST", "/admin/users", n);
  }

  updateAccount(id: UUID, change: { role?: string; status?: string }) {
    return this.request<{ ok: boolean }>("PATCH", `/admin/users/${id}`, change);
  }

  passwordLink(id: UUID) {
    return this.request<{ link: PasswordLink }>("POST", `/admin/users/${id}/password-link`, {});
  }

  // forgotPassword asks, signed out, for a link to set a new password.
  forgotPassword(handle: string) {
    return this.request<{ ok: boolean }>("POST", "/auth/forgot-password", { handle });
  }

  passwordRequests() {
    return this.request<{ requests: PasswordRequest[] }>("GET", "/admin/password-requests");
  }

  dismissPasswordRequest(id: UUID) {
    return this.request<void>("DELETE", `/admin/password-requests/${id}`);
  }

  signOutAccount(id: UUID) {
    return this.request<{ sessions: number }>("POST", `/admin/users/${id}/sign-out`, {});
  }

  // sessions are where one is signed in; userSessions are someone's, for
  // admins.
  sessions() {
    return this.request<{ sessions: SessionInfo[] }>("GET", "/me/sessions");
  }

  // An agent of one's own, or, for an admin, someone else's (ownerID).
  makeAgent(handle: string, displayName: string, ownerID?: UUID) {
    return this.request<{ agent: Principal }>("POST", "/agents", { handle, display_name: displayName, owner_id: ownerID });
  }

  setAgentStatus(id: UUID, status: "active" | "suspended") {
    return this.request<{ ok: boolean }>("PUT", `/agents/${id}/status`, { status });
  }

  agentTokens(id: UUID) {
    return this.request<{ tokens: AgentToken[]; scopes: string[] }>("GET", `/agents/${id}/tokens`);
  }

  // The token is in the answer once, and never again.
  issueAgentToken(id: UUID, name: string, scopes: string[], ttlDays: number) {
    return this.request<{ token: string; info: AgentToken }>("POST", `/agents/${id}/tokens`, { name, scopes, ttl_days: ttlDays });
  }

  revokeAgentToken(id: UUID, token: UUID) {
    return this.request<{ ok: boolean }>("DELETE", `/agents/${id}/tokens/${token}`);
  }

  // The API tokens one manages: one's own and one's agents', or, for an
  // admin with all, every one; the agents one may give tokens; and whether
  // people may make personal ones.
  apiTokens(all = false) {
    return this.request<{ tokens: APIToken[]; scopes: string[]; agents: UUID[]; personal_tokens: boolean }>("GET", `/tokens${all ? "?all=true" : ""}`);
  }

  // A personal token, for one's scripts: in the answer once, and never again.
  issuePersonalToken(name: string, scopes: string[], ttlDays: number) {
    return this.request<{ token: string; info: APIToken }>("POST", "/me/tokens", { name, scopes, ttl_days: ttlDays });
  }

  revokeAPIToken(id: UUID) {
    return this.request<{ ok: boolean }>("DELETE", `/tokens/${id}`);
  }

  endSession(id: UUID) {
    return this.request<{ ok: boolean }>("DELETE", `/me/sessions/${id}`);
  }

  userSessions(user: UUID) {
    return this.request<{ sessions: SessionInfo[] }>("GET", `/admin/users/${user}/sessions`);
  }

  endUserSession(user: UUID, id: UUID) {
    return this.request<{ ok: boolean }>("DELETE", `/admin/users/${user}/sessions/${id}`);
  }

  signOutOthers() {
    return this.request<{ sessions: number }>("POST", "/me/sign-out-others", {});
  }

  customEmoji() {
    return this.request<{ emoji: CustomEmoji[] }>("GET", "/emoji");
  }

  // addEmoji sends the image as the body, as upload does.
  async addEmoji(name: string, image: Blob) {
    const res = await this.fetchImpl(`${this.base}/emoji?name=${encodeURIComponent(name)}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${this.token}`, "Accept-Language": language, "Content-Type": image.type || "application/octet-stream" },
      body: image,
    });
    const data = (await res.json().catch(() => ({}))) as { emoji?: CustomEmoji; error?: { code: string; message: string } };
    if (!res.ok || !data.emoji) throw new ApiError(res.status, data.error?.code ?? "upload_failed", data.error?.message ?? res.statusText);
    return data.emoji;
  }

  removeEmoji(name: string) {
    return this.request<{ ok: boolean }>("DELETE", `/emoji/${encodeURIComponent(name)}`);
  }

  // emojiImage fetches a custom emoji's image, which needs the token.
  async avatarImage(id: UUID): Promise<Blob> {
    const res = await this.fetchImpl(`${this.base}/principals/${id}/avatar`, { headers: { Authorization: `Bearer ${this.token}` } });
    if (!res.ok) throw new ApiError(res.status, "http_" + res.status, res.statusText);
    return res.blob();
  }

  // setAvatar sends the photo as the body, as upload does.
  async setAvatar(image: Blob) {
    const res = await this.fetchImpl(`${this.base}/me/avatar`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${this.token}`, "Accept-Language": language, "Content-Type": image.type || "application/octet-stream" },
      body: image,
    });
    const data = (await res.json().catch(() => ({}))) as { principal?: Principal; error?: { code: string; message: string } };
    if (!res.ok || !data.principal) throw new ApiError(res.status, data.error?.code ?? "upload_failed", data.error?.message ?? res.statusText);
    return data.principal;
  }

  removeAvatar() {
    return this.request<{ principal: Principal }>("DELETE", "/me/avatar");
  }

  async emojiImage(name: string): Promise<Blob> {
    const res = await this.fetchImpl(`${this.base}/emoji/${encodeURIComponent(name)}`, { headers: { Authorization: `Bearer ${this.token}` } });
    if (!res.ok) throw new ApiError(res.status, "http_" + res.status, res.statusText);
    return res.blob();
  }

  groups() {
    return this.request<{ groups: Group[] }>("GET", "/groups");
  }

  createGroup(g: GroupChange & { handle: string; name: string }) {
    return this.request<{ group: Group; not_added: GroupMiss[] }>("POST", "/admin/groups", g);
  }

  updateGroup(id: UUID, g: GroupChange) {
    return this.request<{ group: Group; not_added: GroupMiss[] }>("PATCH", `/admin/groups/${id}`, g);
  }

  deleteGroup(id: UUID) {
    return this.request<Record<string, never>>("DELETE", `/admin/groups/${id}`);
  }

  pins(channel: UUID) {
    return this.request<{ pins: Pin[] }>("GET", `/channels/${channel}/pins`);
  }

  bookmarks(channel: UUID) {
    return this.request<{ bookmarks: Bookmark[] }>("GET", `/channels/${channel}/bookmarks`);
  }

  addBookmark(channel: UUID, b: { title: string; url: string }) {
    return this.request<{ bookmark: Bookmark }>("POST", `/channels/${channel}/bookmarks`, b);
  }

  updateBookmark(channel: UUID, id: UUID, b: { title: string; url: string }) {
    return this.request<{ bookmark: Bookmark }>("PATCH", `/channels/${channel}/bookmarks/${id}`, b);
  }

  removeBookmark(channel: UUID, id: UUID) {
    return this.request<{ ok: boolean }>("DELETE", `/channels/${channel}/bookmarks/${id}`);
  }

  setPinned(channel: UUID, message: UUID, pinned: boolean) {
    return this.request<{ pinned: boolean }>(pinned ? "PUT" : "DELETE", `/channels/${channel}/messages/${message}/pin`);
  }

  files(q: FileQuery) {
    const p = new URLSearchParams(Object.entries(q).filter((e): e is [string, string] => !!e[1]));
    return this.request<{ files: PostedFile[]; next_cursor: string }>("GET", `/files${p.size ? "?" + p : ""}`);
  }

  // deleteFile deletes one's own file, or as an admin anyone's, from every
  // message it is in.
  deleteFile(id: UUID) {
    return this.request<{ ok: boolean }>("DELETE", `/files/${id}`);
  }

  channelFiles(channel: UUID, before?: string) {
    return this.request<{ files: ChannelFile[] }>("GET", `/channels/${channel}/files` + (before ? `?before=${encodeURIComponent(before)}` : ""));
  }

  saved(before?: string) {
    return this.request<{ saved: SavedMessage[] }>("GET", "/saved" + (before ? `?before=${encodeURIComponent(before)}` : ""));
  }

  savedIDs() {
    return this.request<{ ids: UUID[] }>("GET", "/saved/ids");
  }

  setSaved(channel: UUID, message: UUID, saved: boolean) {
    return this.request<{ saved: boolean }>(saved ? "PUT" : "DELETE", `/channels/${channel}/messages/${message}/saved`);
  }

  followedThreads(before?: string) {
    return this.request<{ threads: FollowedThread[] }>("GET", "/threads" + (before ? `?before=${encodeURIComponent(before)}` : ""));
  }

  unreadThreads() {
    return this.request<{ unread: number }>("GET", "/threads/unread");
  }

  markThreadRead(channel: UUID, root: UUID, seq: number) {
    return this.request<Record<string, never>>("POST", `/channels/${channel}/threads/${root}/read`, { seq });
  }

  memberList(id: UUID) {
    return this.request<{ members: { principal_id: UUID; role: string; joined_at: string }[] }>("GET", `/channels/${id}/member-list`);
  }

  addMember(id: UUID, principal_id: UUID) {
    return this.request<Record<string, never>>("POST", `/channels/${id}/members`, { principal_id });
  }

  removeMember(id: UUID, principal: UUID) {
    return this.request<Record<string, never>>("DELETE", `/channels/${id}/members/${principal}`);
  }

  members(channel: UUID) {
    return this.request<{ members: UUID[] }>("GET", `/channels/${channel}/members`);
  }

  // ---- tasks (docs/specs/tasks.md) ----

  getTask(id: UUID) {
    return this.request<{ task: Task }>("GET", `/tasks/${id}`);
  }

  myTasks() {
    return this.request<{ tasks: Task[] }>("GET", "/tasks?limit=100");
  }

  taskEvents(id: UUID) {
    return this.request<{ events: TaskEvent[] }>("GET", `/tasks/${id}/events`);
  }

  capabilities() {
    return this.request<{ capabilities: Capability[] }>("GET", "/capabilities");
  }

  // createTask asks for a capability's work; a repeat with the same key gets
  // the task the first made.
  createTask(task: NewTask, key: string) {
    return this.request<{ task: Task }>("POST", "/tasks", task, { "Idempotency-Key": key });
  }

  // threadTasks are the tasks linked to a thread.
  threadTasks(channel: UUID, root: UUID) {
    return this.request<{ tasks: Task[] }>("GET", `/tasks?channel_id=${channel}&thread_root_id=${root}&limit=50`);
  }

  // taskPools are the pools one may send tasks to by name: every pool for
  // admins, one's own for others.
  taskPools() {
    return this.request<{ pools: { id: UUID; name: string; suspended_at?: string; offers: { capability: string; approved_by: UUID | null }[] }[] }>("GET", "/pools");
  }

  acceptTask(id: UUID) {
    return this.request<{ task: Task; lease: { epoch: number } }>("POST", `/tasks/${id}/accept`);
  }

  completeTask(id: UUID, epoch: number, output: unknown) {
    return this.request<{ task: Task }>("POST", `/tasks/${id}/complete`, { lease_epoch: epoch, output });
  }

  failTask(id: UUID, epoch: number, code: string, message: string) {
    return this.request<{ task: Task }>("POST", `/tasks/${id}/fail`, { lease_epoch: epoch, error: { code, message, retryable: false } });
  }

  provideInput(id: UUID, response: unknown) {
    return this.request<{ task: Task }>("POST", `/tasks/${id}/input`, { response });
  }

  cancelTask(id: UUID, reason: string) {
    return this.request<{ task: Task }>("POST", `/tasks/${id}/cancel`, { reason });
  }

  retryTask(id: UUID) {
    return this.request<{ task: Task }>("POST", `/tasks/${id}/retry`);
  }

  workflows() {
    return this.request<{ workflows: Workflow[] }>("GET", "/workflows");
  }

  createWorkflow(definition: unknown) {
    return this.request<{ workflow: Workflow; webhook_url?: string }>("POST", "/workflows", definition);
  }

  updateWorkflow(id: UUID, definition: unknown) {
    return this.request<{ workflow: Workflow }>("PUT", `/workflows/${id}`, definition);
  }

  startRun(id: UUID, inputs: Record<string, unknown> = {}) {
    return this.request<{ run: WorkflowRun }>("POST", `/workflows/${id}/runs`, { inputs });
  }

  runs(id: UUID) {
    return this.request<{ runs: WorkflowRun[] }>("GET", `/workflows/${id}/runs?limit=20`);
  }

  run(id: UUID) {
    return this.request<{ run: WorkflowRun }>("GET", `/workflow-runs/${id}`);
  }

  cancelRun(id: UUID) {
    return this.request<{ run: WorkflowRun }>("POST", `/workflow-runs/${id}/cancel`);
  }

  blockAction(channel: UUID, message: UUID, action: { block_id: string; action_id: string; value: string }) {
    return this.request<{ ok: boolean }>("POST", `/channels/${channel}/messages/${message}/actions`, action);
  }

  budgets() {
    return this.request<{ budgets: Budget[] }>("GET", "/budgets");
  }

  commands() {
    return this.request<{ commands: { command: string; app_name: string; description: string; usage_hint: string }[] }>("GET", "/commands");
  }

  runCommand(channel: UUID, command: string, text: string, threadRootID: UUID | null) {
    return this.request<{ text?: string; blocks?: unknown; posted: boolean }>("POST", `/channels/${channel}/commands`, {
      command,
      text,
      thread_root_id: threadRootID,
    });
  }

  inbox() {
    return this.request<{ items: InboxItem[] }>("GET", "/inbox");
  }

  // inboxSeen says one looked at the inbox, so reactions until now are read.
  inboxSeen() {
    return this.request<{ ok: boolean }>("POST", "/inbox/seen");
  }

  catchUp(channel: UUID, threadRootID?: UUID) {
    return this.request<CatchUp>("POST", `/channels/${channel}/summary`, threadRootID ? { thread_root_id: threadRootID } : {});
  }

  // supportBundle fetches a support bundle, a .tar.gz for an admin to read
  // and hand over.
  async supportBundle(): Promise<{ blob: Blob; name: string }> {
    const res = await this.fetchImpl(this.base + "/admin/support-bundle", {
      headers: this.token ? { Authorization: `Bearer ${this.token}` } : {},
    });
    if (!res.ok) {
      const data = (await res.json().catch(() => ({}))) as { error?: { code: string; message: string } };
      throw new ApiError(res.status, data.error?.code ?? "http_" + res.status, data.error?.message ?? res.statusText);
    }
    const name = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "")?.[1] ?? "lantern-support.tar.gz";
    return { blob: await res.blob(), name };
  }

  license() {
    return this.request<LicenseStatus>("GET", "/admin/license");
  }

  installLicense(token: string, replace = false) {
    return this.request<LicenseStatus>("PUT", "/admin/license", { token, replace });
  }

  submitView(id: UUID, values: Record<string, Record<string, string | string[]>>) {
    return this.request<ViewResult>("POST", `/views/${id}/submit`, { values });
  }

  closeView(id: UUID) {
    return this.request<{ ok: boolean }>("POST", `/views/${id}/close`, {});
  }

  viewAction(id: UUID, a: { block_id: string; action_id: string; value: string }, values: Record<string, Record<string, string | string[]>>) {
    return this.request<{ ok: boolean }>("POST", `/views/${id}/actions`, { ...a, values });
  }

  channelArtifacts(channel: UUID) {
    return this.request<{ artifacts: Artifact[] }>("GET", `/channels/${channel}/artifacts`);
  }

  artifact(id: UUID, version = 0) {
    return this.request<{ artifact: Artifact }>("GET", `/artifacts/${id}${version ? `?version=${version}` : ""}`);
  }

  artifactVersions(id: UUID) {
    return this.request<{ versions: ArtifactVersion[] }>("GET", `/artifacts/${id}/versions`);
  }

  artifactEntries(id: UUID) {
    return this.request<{ entries: ArtifactEntry[] }>("GET", `/artifacts/${id}/kv`);
  }

  updateArtifact(id: UUID, body: string, expectedVersion: number) {
    return this.request<{ artifact: Artifact }>("PUT", `/artifacts/${id}`, { body, expected_version: expectedVersion });
  }

  hostedAgents() {
    return this.request<{ agents: HostedAgent[]; tools: string[]; default_tools: string[]; running?: boolean }>("GET", "/admin/hosted-agents");
  }

  createHostedAgent(a: Partial<HostedAgent>) {
    return this.request<{ agent: HostedAgent }>("POST", "/admin/hosted-agents", a);
  }

  updateHostedAgent(id: UUID, a: HostedAgent) {
    return this.request<{ agent: HostedAgent }>("PUT", `/admin/hosted-agents/${id}`, a);
  }

  mcpServers() {
    return this.request<{ servers: MCPServer[] }>("GET", "/admin/mcp-servers");
  }

  addMCPServer(name: string, url: string, headers: Record<string, string>) {
    return this.request<{ server: MCPServer }>("POST", "/admin/mcp-servers", { name, url, headers });
  }

  // ---- model backends (admins) ----

  modelBackends() {
    return this.request<{ backends: ModelBackend[] }>("GET", "/admin/models");
  }

  addModelBackend(input: ModelBackendInput) {
    return this.request<{ backend: ModelBackend }>("POST", "/admin/models", input);
  }

  updateModelBackend(id: UUID, input: ModelBackendInput) {
    return this.request<{ backend: ModelBackend }>("PATCH", `/admin/models/${id}`, input);
  }

  deleteModelBackend(id: UUID) {
    return this.request<{ ok: boolean }>("DELETE", `/admin/models/${id}`);
  }

  // testModelBackend makes one small call: to a saved backend (id), changed
  // or not by the rest, or to one not saved yet.
  testModelBackend(input: ModelBackendInput & { id?: UUID }) {
    return this.request<ModelTest>("POST", "/admin/models/test", input);
  }

  // ---- your own profile ----

  principal(id: UUID) {
    return this.request<{ principal: Principal }>("GET", `/principals/${id}`);
  }

  preferences() {
    return this.request<{ preferences: Preferences }>("GET", "/me/preferences");
  }

  updateMe(change: { display_name?: string; title?: string; pronouns?: string; time_zone?: string }) {
    return this.request<{ principal: Principal }>("PATCH", "/me", change);
  }

  setStatus(emoji: string, text: string, expires_at: string | null) {
    return this.request<{ principal: Principal }>("PUT", "/me/status", { emoji, text, expires_at });
  }

  setAway(away: boolean) {
    return this.request<{ principal: Principal }>("PUT", "/me/away", { away });
  }

  pauseNotifications(paused_until: string | null) {
    return this.request<{ preferences: Preferences }>("PUT", "/me/notifications", { paused_until });
  }

  following(channel: UUID, root: UUID) {
    return this.request<{ following: boolean }>("GET", `/channels/${channel}/threads/${root}/follow`);
  }

  follow(channel: UUID, root: UUID, on: boolean) {
    return this.request<{ ok: boolean }>(on ? "POST" : "DELETE", `/channels/${channel}/threads/${root}/follow`);
  }

  huddles() {
    return this.request<{ huddles: Huddle[] }>("GET", "/huddles");
  }

  recentHuddles(before?: string, beforeID?: UUID) {
    const q = before ? `?before=${encodeURIComponent(before)}` + (beforeID ? `&before_id=${beforeID}` : "") : "";
    return this.request<{ huddles: PastHuddle[]; has_more: boolean }>("GET", `/huddles/recent${q}`);
  }

  joinHuddle(channel: UUID) {
    return this.request<{ path: string; room: string; token: string }>("POST", `/channels/${channel}/huddle`);
  }

  setTheme(theme: string, appearance: string) {
    return this.request<{ preferences: Preferences }>("PUT", "/me/theme", { theme, appearance });
  }

  setDisplay(text_size: string, density: string, message_layout: string) {
    return this.request<{ preferences: Preferences }>("PUT", "/me/display", { text_size, density, message_layout });
  }

  setLanguage(language: string) {
    return this.request<{ preferences: Preferences }>("PUT", "/me/language", { language });
  }

  setKeywords(keywords: string[]) {
    return this.request<{ preferences: Preferences }>("PUT", "/me/keywords", { keywords });
  }

  setQuietHours(quiet_hours: QuietHours | null) {
    return this.request<{ preferences: Preferences }>("PUT", "/me/quiet-hours", { quiet_hours });
  }

  changePassword(current_password: string, new_password: string) {
    return this.request<Record<string, never>>("POST", "/me/password", { current_password, new_password });
  }

  shortcuts(channel: UUID) {
    return this.request<{ shortcuts: Shortcut[] }>("GET", `/channels/${channel}/shortcuts`);
  }

  runShortcut(workflow: UUID, channel: UUID, message?: UUID) {
    return this.request<{ run: WorkflowRun }>("POST", `/workflows/${workflow}/shortcut`, { channel_id: channel, message_id: message });
  }

  draftWorkflow(description: string) {
    return this.request<{ definition: unknown }>("POST", "/workflows/draft", { description });
  }

  discoverySearch(matter: Matter) {
    return this.request<{ count: number; items: DiscoveryItem[] }>("POST", "/admin/discovery/search", matter);
  }

  async discoveryExport(matter: Matter, format: "jsonl" | "eml" | "loadfile"): Promise<{ blob: Blob; name: string }> {
    const res = await this.fetchImpl(this.base + "/admin/discovery/export?format=" + format, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}) },
      body: JSON.stringify(matter),
    });
    if (!res.ok) {
      const data = (await res.json().catch(() => ({}))) as { error?: { code: string; message: string } };
      throw new ApiError(res.status, data.error?.code ?? "http_" + res.status, data.error?.message ?? res.statusText);
    }
    const name = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "")?.[1] ?? "matter";
    return { blob: await res.blob(), name };
  }

  features() {
    return this.request<{ tiers: Record<string, boolean>; huddles?: boolean }>("GET", "/features");
  }

  compose(req: { action: "draft" | "rewrite" | "translate"; text: string; channel_id: UUID; thread_root_id?: UUID | null; language?: string; tone?: string }) {
    return this.request<{ text: string }>("POST", "/compose", req);
  }

  workspaceSettings() {
    return this.request<WorkspaceSettings>("GET", "/admin/workspace");
  }

  linkPreviews(channel: UUID, message: UUID) {
    return this.request<{ previews: LinkPreview[] }>("GET", `/channels/${channel}/messages/${message}/previews`);
  }

  updateWorkspace(change: { name?: string; workflow_http_hosts?: string[]; link_previews?: boolean; theme?: string; recall_seconds?: number; channel_creation?: "everyone" | "admins"; personal_tokens?: boolean; reauth_minutes?: number }) {
    return this.request<WorkspaceSettings>("PATCH", "/admin/workspace", change);
  }

  directoryGroups() {
    return this.request<{ groups: DirectoryGroup[] }>("GET", "/admin/directory-groups");
  }

  mapDirectoryGroups(mapping: { roles: Record<string, string>; channels: Record<string, string[]> }) {
    return this.request<{ groups: DirectoryGroup[] }>("PUT", "/admin/directory-groups", mapping);
  }

  setRetention(days: number | null) {
    return this.request<Record<string, never>>("PUT", "/admin/retention", { days });
  }

  setTraceRetention(days: number | null) {
    return this.request<Record<string, never>>("PUT", "/admin/trace-retention", { days });
  }

  dlpRules() {
    return this.request<{ rules: DLPRule[]; detectors: string[] }>("GET", "/admin/dlp/rules");
  }

  createDLPRule(rule: Omit<DLPRule, "id" | "created_at">) {
    return this.request<{ rule: DLPRule }>("POST", "/admin/dlp/rules", rule);
  }

  deleteDLPRule(id: UUID) {
    return this.request<Record<string, never>>("DELETE", `/admin/dlp/rules/${id}`);
  }

  health() {
    return this.request<HealthReport>("GET", "/admin/health");
  }

  fleet() {
    return this.request<Fleet>("GET", "/fleet");
  }

  suspendPool(id: UUID, suspend: boolean) {
    return this.request<{ ok: boolean }>("POST", `/pools/${id}/${suspend ? "suspend" : "resume"}`);
  }

  // ---- capabilities, pools and their offers, and the task policy (tasks spec 3 and 9) ----

  capabilityCards() {
    return this.request<{ capabilities: CapabilityCard[] }>("GET", "/capabilities");
  }

  // An admin's card is approved at once; anyone else's is proposed.
  registerCapability(card: Partial<CapabilityCard> & { name: string }) {
    return this.request<{ capability: CapabilityCard }>("POST", "/capabilities", card);
  }

  approveCapability(name: string) {
    return this.request<{ ok: boolean }>("POST", "/capabilities/approve", { name });
  }

  pools() {
    return this.request<{ pools: AgentPool[] }>("GET", "/pools");
  }

  createPool(name: string) {
    return this.request<{ pool: AgentPool }>("POST", "/pools", { name });
  }

  // An agent is in one pool at a time; adding it to another moves it.
  addPoolAgent(pool: UUID, agentID: UUID) {
    return this.request<{ ok: boolean }>("POST", `/pools/${pool}/agents`, { agent_id: agentID });
  }

  offerCapability(pool: UUID, capability: string) {
    return this.request<{ ok: boolean }>("POST", `/pools/${pool}/offers`, { capability });
  }

  // Refused with own_pool for an admin who owns the pool.
  approveOffer(pool: UUID, capability: string) {
    return this.request<{ ok: boolean }>("POST", `/pools/${pool}/offers/approve`, { capability });
  }

  // managedAgents are the agents one manages: every agent, for an admin.
  managedAgents() {
    return this.request<{ agents: ManagedAgent[] }>("GET", "/agents");
  }

  taskPolicy() {
    return this.request<{ policy: TaskPolicy }>("GET", "/admin/task-policy");
  }

  setTaskPolicy(policy: TaskPolicy) {
    return this.request<{ policy: TaskPolicy }>("PUT", "/admin/task-policy", policy);
  }

  answer(q: string, channelID?: UUID) {
    return this.request<SearchAnswer>("POST", "/search/answer", { q, channel_id: channelID ?? null });
  }

  search(q: string, channelID?: UUID) {
    const ch = channelID ? `&channel_id=${channelID}` : "";
    // Typed date filters (on:today) are read in this browser's time zone.
    const tz = `&tz_offset=${-new Date().getTimezoneOffset()}`;
    return this.request<{ hits: SearchHit[]; has_more: boolean }>("GET", `/search?q=${encodeURIComponent(q)}${ch}${tz}`);
  }
}
